import { useState, useEffect, useRef } from 'react';
import { X, Copy, Check, Download } from 'lucide-react';
import useTranslation from '@/hooks/useTranslation';
import useCopyState from './useCopyState';
import { cn } from '@/lib/utils';
import type { ResearchArtifact } from '@/reactQuery/researchAssistant';
import ArtifactQuoteCard from './ArtifactQuoteCard';
import ArtifactDataTable from './ArtifactDataTable';
import ArtifactThemeSummary from './ArtifactThemeSummary';
import ArtifactChartBlock from './ArtifactChartBlock';
import ArtifactMarkdownReport from './ArtifactMarkdownReport';

interface ArtifactPanelProps {
  artifacts: ResearchArtifact[];
  onClose: () => void;
}

function ArtifactPanel({ artifacts, onClose }: ArtifactPanelProps) {
  const { lang } = useTranslation();
  // Initial index defaults to 0 if there are artifacts, otherwise 0 (safe).
  // Math.max prevents -1 when artifacts.length === 0 (we early-return below
  // but useState fires before the early return).
  const [activeIdx, setActiveIdx] = useState(() => Math.max(0, artifacts.length - 1));
  const [copied, triggerCopied] = useCopyState();
  const prevLengthRef = useRef(artifacts.length);

  // Capture the element that had focus when the panel mounted so we can
  // restore focus when it closes — important for keyboard users who would
  // otherwise be dropped on `<body>`.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  // Sprint A audit fix (H-5): move focus INTO the panel when it first
  // mounts so screen reader and keyboard users know it has opened.
  // Previously focus stayed in the chat textarea and the panel appeared
  // silently. The close button is the safest landing target — it's
  // always visible, doesn't trigger any data action on focus, and
  // becomes the start of the natural Tab order through the panel.
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement;
    // Defer the focus until after the panel has rendered so the element
    // is in the DOM. requestAnimationFrame is enough — no need for
    // setTimeout.
    requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });
    return () => {
      // Restore focus on unmount if the previously-focused element is
      // still in the document
      const prev = previouslyFocusedRef.current;
      if (prev && document.body.contains(prev)) {
        try {
          prev.focus();
        } catch {
          // ignore focus failures
        }
      }
    };
  }, []);

  // Auto-switch to the newest artifact when one is added (e.g., during streaming).
  // We only follow growth — if the user manually switches tabs, that selection
  // is preserved until a new artifact arrives.
  useEffect(() => {
    if (artifacts.length > prevLengthRef.current) {
      setActiveIdx(artifacts.length - 1);
    }
    prevLengthRef.current = artifacts.length;
  }, [artifacts.length]);

  if (artifacts.length === 0) return null;

  // Clamp activeIdx to the valid range — guards against the user removing
  // artifacts (not currently possible, but defensive).
  const clampedIdx = Math.max(0, Math.min(activeIdx, artifacts.length - 1));
  const active = artifacts[clampedIdx];

  /**
   * Copy the active artifact as human-readable formatted text.
   * Each artifact type renders to a researcher-friendly format suitable
   * for pasting into documents, emails, or presentations.
   */
  const handleCopyAll = () => {
    if (!active) return;
    let text = `${active.title}\n\n`;
    switch (active.type) {
      case 'quote_collection':
        text += (active.data?.quotes || [])
          .map(
            (q: any) =>
              `"${q.text}" — Respondent ${q.respondent_index}${
                q.respondent_metadata
                  ? `, ${Object.values(q.respondent_metadata).filter(Boolean).join(', ')}`
                  : ''
              }`,
          )
          .join('\n\n');
        break;
      case 'data_table': {
        const cols = active.data?.columns || [];
        const rows = active.data?.rows || [];
        text += `${cols.map((c: any) => c.label).join('\t')}\n`;
        text += rows
          .map((row: any) => cols.map((c: any) => row[c.key] ?? '').join('\t'))
          .join('\n');
        break;
      }
      case 'theme_summary':
        text += (active.data?.themes || []).map((t: any) => `${t.title}\n${t.body}`).join('\n\n');
        break;
      case 'bar_chart':
        text += (active.data?.series || []).map((s: any) => `${s.name}: ${s.value}`).join('\n');
        break;
      case 'markdown_report':
        text += active.data?.content || '';
        break;
      default:
        text += JSON.stringify(active.data, null, 2);
    }
    navigator.clipboard.writeText(text).then(() => triggerCopied()).catch(() => {});
  };

  // Plain function (not useCallback) to keep the hook ordering consistent
  // with the early-return at line 68. handleCopyAll above follows the same
  // pattern. Memoization here is not worth the rules-of-hooks risk: this
  // function is only used in a single button onClick and the parent
  // component re-renders rarely.
  const handleExportSession = () => {
    // Escape pipe characters in markdown table cells so a quote like "good | bad"
    // doesn't break the table structure.
    const escapeCell = (v: unknown): string =>
      String(v ?? '')
        .replace(/\|/g, '\\|')
        .replace(/\n/g, ' ');

    const sections = artifacts.map((a) => {
      let content = '';
      switch (a.type) {
        case 'quote_collection':
          content = (a.data?.quotes || [])
            .map((q: any) => `> "${q.text}" — Respondent ${q.respondent_index}`)
            .join('\n\n');
          break;
        case 'data_table':
          content = [
            (a.data?.columns || []).map((c: any) => escapeCell(c.label)).join(' | '),
            (a.data?.columns || []).map(() => '---').join(' | '),
            ...(a.data?.rows || []).map((row: any) =>
              (a.data?.columns || []).map((c: any) => escapeCell(row[c.key])).join(' | '),
            ),
          ].join('\n');
          break;
        case 'theme_summary':
          content = (a.data?.themes || [])
            .map((t: any) => `### ${t.title}\n\n${t.body}`)
            .join('\n\n');
          break;
        case 'bar_chart':
          content = (a.data?.series || []).map((s: any) => `- ${s.name}: ${s.value}`).join('\n');
          break;
        case 'markdown_report':
          content = a.data?.content || '';
          break;
        default:
          content = JSON.stringify(a.data, null, 2);
      }
      return `## ${a.title}\n\n${content}`;
    });

    const markdown = `# Research Session Export\n\n${sections.join('\n\n---\n\n')}`;
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `research-session-${new Date().toISOString().slice(0, 10)}.md`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const renderArtifact = (artifact: ResearchArtifact) => {
    switch (artifact.type) {
      case 'quote_collection':
        return <ArtifactQuoteCard title={artifact.title} data={artifact.data} />;
      case 'data_table':
        return <ArtifactDataTable title={artifact.title} data={artifact.data} />;
      case 'theme_summary':
        return <ArtifactThemeSummary title={artifact.title} data={artifact.data} />;
      case 'bar_chart':
        return <ArtifactChartBlock title={artifact.title} data={artifact.data} />;
      case 'markdown_report':
        return <ArtifactMarkdownReport title={artifact.title} data={artifact.data} />;
      default:
        return (
          <pre className="text-xs text-neutral-600 bg-neutral-50 p-3 rounded-lg overflow-auto">
            {JSON.stringify(artifact.data, null, 2)}
          </pre>
        );
    }
  };

  return (
    <div className="flex flex-col h-full border-l border-neutral-200 bg-white">
      {/* Header. Fixed h-14 (56px) so the three Research Assistant
          columns (threads, chat, artifacts) have aligned header bars. */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-neutral-200 shrink-0">
        <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">
          {lang.get('msg.raArtifacts')}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleExportSession}
            className="p-2 text-neutral-500 hover:text-neutral-700 rounded hover:bg-neutral-100 transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
            title={lang.get('msg.exportAllArtifacts')}
            aria-label={lang.get('msg.exportAllArtifacts')}
          >
            <Download className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={handleCopyAll}
            className="p-2 text-neutral-500 hover:text-neutral-700 rounded hover:bg-neutral-100 transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
            title={copied ? lang.get('msg.copied') : lang.get('msg.copyArtifact')}
            aria-label={copied ? lang.get('msg.copied') : lang.get('msg.copyArtifact')}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-500" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label={lang.get('msg.close')}
            className="p-2 text-neutral-500 hover:text-neutral-700 rounded hover:bg-neutral-100 transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Tabs — Sprint A audit fix: added ARIA tablist semantics so
          screen readers can announce the selected tab. Without these,
          VoiceOver and NVDA announce only "button" with no indication
          of which artifact is currently displayed. */}
      {artifacts.length > 1 && (
        <div
          role="tablist"
          aria-label="Artifacts"
          className="flex gap-0.5 px-3 py-1.5 border-b border-neutral-100 overflow-x-auto shrink-0"
        >
          {artifacts.map((artifact, idx) => {
            const isSelected = idx === activeIdx;
            return (
              <button
                key={artifact.artifact_id}
                type="button"
                role="tab"
                aria-selected={isSelected}
                aria-controls={`artifact-panel-${artifact.artifact_id}`}
                aria-label={artifact.title}
                id={`artifact-tab-${artifact.artifact_id}`}
                tabIndex={isSelected ? 0 : -1}
                onClick={() => setActiveIdx(idx)}
                // Title attribute surfaces the full label on hover/focus so
                // truncated tabs remain discoverable. Both visible label
                // and aria-label use the unmodified title; only the rendered
                // text is shortened.
                title={artifact.title}
                className={cn(
                  'text-[11px] font-medium px-2.5 py-1 rounded-md whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300',
                  isSelected
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'text-neutral-500 hover:bg-neutral-100',
                )}
              >
                {artifact.title.length > 25 ? `${artifact.title.slice(0, 25)}...` : artifact.title}
              </button>
            );
          })}
        </div>
      )}

      {/* Content */}
      <div
        role="tabpanel"
        id={active ? `artifact-panel-${active.artifact_id}` : undefined}
        aria-labelledby={active ? `artifact-tab-${active.artifact_id}` : undefined}
        className="flex-1 overflow-y-auto px-4 py-4"
      >
        {active && renderArtifact(active)}
      </div>
    </div>
  );
}

export default ArtifactPanel;
